import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PageHero, GoldButton, SectionEyebrow, FadedBackdrop } from "@/components/ui-bits";
import { IMG } from "@/constants/images";

// SEO NOTE: Every question below is phrased as a natural-language query a real
// person would type into Google, Bing, ChatGPT, or Perplexity. Each answer opens
// with a direct restated subject (Featured Snippet best practice) and stays in
// the 40–90 word sweet spot for AI Overview / rich-result eligibility.

// The FAQ is organized into the 5 Pillars of OGO Excellence. Each pillar
// contains one or more sub-sections (rendered as sub-headings inside the
// pillar block). All Q&As feed a single FAQPage JSON-LD structured-data block
// at the top of the page so rich-result eligibility is preserved.

const PILLARS = [
  {
    pillar: "Pillar 1 · Mobile, RON & On-Demand Notary",
    intro:
      "General notarizations, mobile travel across Brevard County & Florida statewide, and Remote Online Notarization (RON) for signers anywhere in the U.S.",
    sections: [
      {
        subtitle: "General Notarization",
        items: [
          {
            q: "What is a Notary Public and what can they do in Florida?",
            a: "A Florida Notary Public is a state-commissioned officer authorized to witness signatures, administer oaths and affirmations, take acknowledgments, and certify copies of certain documents. A notary verifies the signer's identity, confirms the signer is signing willingly and knowingly, and records the notarial act in a journal. Florida notaries are not attorneys and cannot give legal advice or draft documents.",
          },
          {
            q: "What ID do I need to bring to a notary appointment?",
            a: "Bring one unexpired, government-issued photo ID — such as a Florida driver's license, Florida state ID card, U.S. passport, U.S. passport card, or military ID. The name on your ID must match the name on the document exactly. If your name has legally changed, bring supporting documentation (marriage certificate, court order).",
          },
          {
            q: "Can a notary give me legal advice or help me choose the right form?",
            a: "No. A Florida Notary Public is not an attorney and is legally prohibited from giving legal advice, drafting legal documents, selecting forms, or interpreting document contents. If you need legal guidance on wills, trusts, real estate contracts, or immigration forms, please consult a licensed Florida attorney.",
          },
          {
            q: "Do I need witnesses for my document, and can OGO Notary provide them?",
            a: "Some Florida documents — including wills, most healthcare surrogate designations, and certain deeds — require impartial witnesses in addition to a notary. OGO Notary can arrange one or two impartial witnesses when scheduled in advance for an additional fee. Family members and beneficiaries generally cannot serve as impartial witnesses.",
          },
          {
            q: "What happens if I don't sign the document in front of the notary?",
            a: "Under Florida law, an acknowledgment can be taken after signing if the signer personally appears before the notary and acknowledges the signature is their own. For a jurat (sworn statement), however, the signer must physically sign in front of the notary and take an oath. Bring the document unsigned unless your recipient specifically requests otherwise — it removes any ambiguity.",
          },
          {
            q: "Can OGO Notary make a certified copy of my document?",
            a: "Florida notaries can certify copies of a limited set of documents — generally, documents that are not eligible to be certified by the government agency that issued them. Common examples include private documents like corporate records or family photographs. OGO Notary cannot certify copies of birth certificates, passports, or court records; those must be requested from the issuing agency.",
          },
        ],
      },
      {
        subtitle: "Mobile Notary in Brevard County, Florida",
        items: [
          {
            q: "Where can I find a mobile notary in Brevard County, Florida?",
            a: "OGO Notary & Signing Services is a mobile notary based on Florida's Space Coast serving all of Brevard County. We travel to your home, office, hospital, senior-care facility, or job site throughout the county — including Cocoa Beach, Melbourne, Palm Bay, Titusville, Merritt Island, Viera, Rockledge, Satellite Beach, Cape Canaveral, and Suntree. Nights, weekends, and same-day appointments are available.",
          },
          {
            q: "Do you provide mobile notary services in Cocoa Beach, Melbourne, and Palm Bay?",
            a: "Yes. OGO Notary provides mobile notary services throughout Cocoa Beach, Melbourne, Palm Bay, Titusville, Merritt Island, Viera, Rockledge, Satellite Beach, Cape Canaveral, Cocoa, Indialantic, Indian Harbour Beach, and every community in Brevard County, Florida. We come to you — no need to drive to a bank or shipping store.",
          },
          {
            q: "How much does a mobile notary cost in Florida?",
            a: "In Florida, the notarial act itself is capped at the statutory maximum per notarized signature (currently $10 per signature). OGO Notary adds a transparent, up-front travel and convenience fee based on distance, time of day, and urgency. All pricing is disclosed in writing before the appointment — no surprises at the table.",
          },
          {
            q: "Can a mobile notary come to a hospital, hospice, or assisted living facility?",
            a: "Yes. OGO Notary regularly visits hospitals, hospice facilities, rehab centers, and assisted-living communities across Brevard County to notarize healthcare surrogate designations, durable powers of attorney, living wills, and other urgent documents at the bedside. We coordinate with facility staff and confirm the signer's willingness and awareness in accordance with Florida law.",
          },
          {
            q: "Do you offer same-day, evening, or weekend mobile notary appointments?",
            a: "Yes. Evening and weekend mobile-notary appointments are standard at OGO Notary, and same-day / rush appointments are accepted subject to availability. Rush and after-hours convenience fees are quoted transparently up front. Call (855) 505-4722 or email Greg@ogonotary.com to check current availability.",
          },
        ],
      },
      {
        subtitle: "Remote Online Notary (RON) in Florida",
        items: [
          {
            q: "Where can I find a Remote Online Notary in Florida?",
            a: "OGO Notary & Signing Services is a Florida-commissioned Remote Online Notary (RON) serving signers anywhere in the world. Our RON sessions run on Florida state-approved audio-video technology with multi-factor identity verification (KBA + credential analysis) and encrypted, tamper-evident recording. Book a RON session at OGONotary.com or email Greg@ogonotary.com.",
          },
          {
            q: "Is Remote Online Notarization legal in Florida?",
            a: "Yes. Remote Online Notarization is fully authorized under Florida Statutes Chapter 117, Part II. A Florida-commissioned RON notary must be physically located in Florida at the time of the act, but the signer may be located anywhere in the United States or internationally — subject to the laws of the signer's location and the receiving jurisdiction.",
          },
          {
            q: "Can I get a document notarized online if I live in another state or country?",
            a: "Yes. Under a Florida RON commission, the remote signer may be located anywhere in the world — including out-of-state, overseas, deployed military, or expat scenarios — provided the transaction and the receiving jurisdiction accept a Florida RON notarization. Some jurisdictions and document types are ineligible; OGO Notary confirms eligibility before scheduling.",
          },
          {
            q: "What do I need to complete a Remote Online Notarization session?",
            a: "For a Florida RON session you need a webcam and microphone, a smartphone to photograph your ID for credential analysis, a valid unexpired government-issued photo ID, and a stable broadband or LTE/5G internet connection. You also complete a short knowledge-based authentication (KBA) quiz to verify your identity before the notary joins.",
          },
          {
            q: "Which documents can and cannot be notarized remotely in Florida?",
            a: "Most everyday documents — powers of attorney, affidavits, real-estate loan packages, corporate resolutions, apostille source documents, and contracts — are eligible for Florida RON. Wills, codicils, and certain trust instruments have specific restrictions or require additional witnessing. OGO Notary confirms document eligibility for RON before scheduling your session.",
          },
        ],
      },
    ],
  },
  {
    pillar: "Pillar 2 · Real Estate, Loan Signing & Estate Planning",
    intro:
      "Mortgage refis, purchase / sale packages, commercial loans, Wills, Living Trusts, and Powers of Attorney — executed with NNA-certified precision.",
    sections: [
      {
        subtitle: "Loan Signing Agent & Real Estate Closings",
        items: [
          {
            q: "How do I find a certified notary signing agent in Brevard County?",
            a: "OGO Notary & Signing Services is an NNA-certified, background-screened notary signing agent serving title companies, escrow officers, mortgage brokers, and law firms across Brevard County and Central Florida. We handle refinance, purchase, seller, reverse mortgage, HELOC, loan modification, deed, and application packages with same-day scan-backs and tracked drop-offs.",
          },
          {
            q: "What types of loan signing packages do you handle?",
            a: "OGO Notary handles refinance, purchase, seller, reverse mortgage, HELOC, second mortgage, loan modification, application, and deed packages — including hybrid e-signings. Every 100+ page packet is audited page-by-page before the appointment, tabbed for signature/initial/notary points, and signed in black ink with no cross-outs.",
          },
          {
            q: "Do you scan documents back to the title company the same day?",
            a: "Yes. Same-day scan-backs and tracked drop-offs are standard on every OGO Notary loan signing assignment. You receive drop confirmation with carrier, label, drop location, and tracking number so your file team can close on time without chasing paperwork.",
          },
          {
            q: "How do you prevent errors on complex 100-page loan packages?",
            a: "Every OGO loan signing follows a pre-flight compliance blueprint: the packet is audited page-by-page ahead of the appointment, signature and initial points are tabbed, required IDs are verified in advance, and the notary signs in black ink with no cross-outs. Post-signing we scan-back before the drop for a second-pass QA.",
          },
          {
            q: "Do you notarize real estate documents for out-of-state buyers or sellers?",
            a: "Yes. OGO Notary serves out-of-state buyers, sellers, and title companies via two paths: (1) in-person mobile notary if the signer is physically in Brevard County or nearby, or (2) Remote Online Notarization (RON) if the signer is anywhere in the U.S. or internationally. RON is ideal for out-of-state sellers, snowbirds, and remote closings that need to happen fast.",
          },
        ],
      },
      {
        subtitle: "Estate Planning — Wills, Trusts & Powers of Attorney",
        items: [
          {
            q: "How do I get a will notarized in Florida?",
            a: "In Florida, a will must be signed by the testator in the presence of two impartial witnesses, and all three signatures must then be acknowledged before a notary using a self-proving affidavit. OGO Notary provides the notary and can arrange two impartial witnesses at the same appointment. Please note: Florida wills generally cannot be signed by RON — an in-person mobile notary appointment is required.",
          },
          {
            q: "Can OGO Notary notarize a Living Trust or Trust Amendment?",
            a: "Yes. OGO Notary regularly notarizes revocable living trusts, trust amendments, trust restatements, pour-over wills, and certifications of trust — either at your home, office, attorney's office, or via RON where the document type allows. Trusts have specific witnessing requirements that vary by document type; we confirm the correct execution formalities before your appointment.",
          },
          {
            q: "What is the difference between a Durable POA and a Healthcare Surrogate?",
            a: "A Durable Power of Attorney (Fla. Stat. Chapter 709) empowers an agent to manage your financial and legal affairs and remains valid if you become incapacitated. A Healthcare Surrogate Designation (Fla. Stat. Chapter 765) empowers a person to make medical decisions on your behalf when you cannot. Most families need both. OGO Notary handles both documents at a single appointment when scheduled together.",
          },
          {
            q: "Can you notarize a Quitclaim Deed or Warranty Deed for a family transfer?",
            a: "Yes. OGO Notary regularly notarizes quitclaim deeds, warranty deeds, life estate deeds, and enhanced life estate (Lady Bird) deeds for intra-family transfers, estate planning, and divorce settlements. Deeds require two witnesses in addition to the notary in Florida — we can arrange the witnesses when scheduled in advance. Please have your attorney or title professional prepare the deed itself.",
          },
        ],
      },
    ],
  },
  {
    pillar: "Pillar 3 · Government & Business Support",
    intro:
      "Secretary of State Apostilles, corporate entity support, commercial proposals, joint venture documents, and Port Canaveral cruise-port signings — trusted by law firms, corporates, and foreign governments.",
    sections: [
      {
        subtitle: "Apostille & International Documents",
        items: [
          {
            q: "How do I get an Apostille for a document in Florida?",
            a: "To get an Apostille on a Florida document, the document must first be properly notarized or issued by a Florida authority, then submitted to the Florida Secretary of State for the Apostille certificate. OGO Notary acts as your Apostille agent — we handle the notarization (if needed), the Secretary of State submission, and the return shipment to you or your recipient.",
          },
          {
            q: "What countries accept a Florida Apostille?",
            a: "A Florida Apostille is accepted in every country that is a party to the 1961 Hague Apostille Convention — approximately 125+ countries including the UK, Mexico, most of Europe and Latin America, Japan, South Korea, and Australia. For non-Hague countries (China, Canada, UAE and others), OGO Notary coordinates chain authentication through the U.S. Department of State and the destination consulate.",
          },
          {
            q: "How long does it take to get an Apostille from the Florida Secretary of State?",
            a: "Standard Florida Secretary of State Apostille processing is currently 5–10 business days, plus courier transit time to and from Tallahassee. Chain authentications for non-Hague countries typically take 3–6 weeks total due to U.S. Department of State and consular processing. OGO Notary provides tracking at every step and rush service where available.",
          },
          {
            q: "What kinds of documents commonly need an Apostille?",
            a: "Documents commonly requiring an Apostille include birth certificates, marriage certificates, death certificates, adoption papers, powers of attorney, corporate formation documents, articles of incorporation, FBI background checks, diplomas, transcripts, and single-status affidavits — typically for international marriage, dual citizenship, foreign real estate, business incorporation abroad, or overseas adoption.",
          },
          {
            q: "Can OGO Notary handle corporate entity formation documents that need Apostille?",
            a: "Yes. OGO Notary regularly handles corporate documents requiring Apostille — Articles of Incorporation, Certificates of Good Standing, corporate resolutions, powers of attorney for foreign subsidiaries, and joint venture agreements. Dr. Byers has personally overseen thousands of multi-national corporate document packages for the U.S. Department of Defense and private industry across dozens of countries.",
          },
        ],
      },
      {
        subtitle: "Cruise-Port Signings — Port Canaveral",
        items: [
          {
            q: "Can OGO Notary meet me at Port Canaveral before my cruise?",
            a: "Yes. OGO Notary meets embarking passengers at Port Canaveral hotels, terminal drop-off areas, pier-side staging zones, and nearby Cocoa Beach / Cape Canaveral / Merritt Island residences. We serve every major cruise line sailing from Port Canaveral — Carnival, Disney, Royal Caribbean, MSC, and Norwegian — with pre-dawn, early-morning, and same-day appointments coordinated around your boarding window. Please book at least 24 hours in advance whenever possible; true last-minute requests are handled subject to availability.",
          },
          {
            q: "Can a minor travel on a cruise with only one parent using a notarized consent letter?",
            a: "Generally, yes — most cruise lines and international itineraries require a notarized Parental Consent for Minor Travel (sometimes called a Child Travel Consent) when a minor sails without both legal parents or guardians. OGO Notary regularly notarizes these consent letters at Port Canaveral–area hotels and residences. Bring the completed consent letter, a valid photo ID for the signing parent, and, when required by the cruise line or destination country, the minor's birth certificate. OGO Notary does not draft the letter — please prepare it in advance or use your cruise line's template.",
          },
          {
            q: "Can you notarize a medical power of attorney or healthcare directive for an elderly cruiser?",
            a: "Yes. Medical powers of attorney, healthcare surrogate designations, HIPAA authorizations, and living wills are frequently notarized for elderly passengers preparing to sail from Port Canaveral. OGO Notary conducts these signings at your Cocoa Beach / Cape Canaveral hotel, private residence, or an accessible port-area location. We verify the signer's identity and confirm they are signing willingly and with apparent awareness, as Florida law requires. Bedside notarizations for hospitalized or recovering passengers are also available across Brevard County.",
          },
          {
            q: "Do you handle last-minute powers of attorney for people about to board a cruise?",
            a: "Yes — last-minute Powers of Attorney (durable POAs, limited/specific POAs for a house closing, tax filing, or vehicle sale while you're at sea) are one of the most common cruise-port requests we handle. Contact us as early as possible with your sailing date, cruise line, terminal, and the document type. Same-day, evening, and pre-dawn Port Canaveral appointments are quoted transparently with any rush or convenience fee disclosed up front. OGO Notary does not draft POAs — please have the document prepared by your attorney or from a reliable Florida-compliant template before we arrive.",
          },
        ],
      },
    ],
  },
  {
    pillar: "Pillar 4 · New Beginnings & Life Milestones",
    intro:
      "Wedding officiant services, marriage certificates, adoption paperwork, and early- and mid-life family milestone documentation.",
    sections: [
      {
        subtitle: "Wedding Officiant & Ministerial Services",
        items: [
          {
            q: "Can you perform both civil and religious wedding ceremonies?",
            a: "Yes. Dr. Byers operates in a dual capacity depending on your preferences. As a Commissioned Florida Notary Public, he can legally officiate and execute state-authorized civil ceremonies. As an Ordained Minister, he can conduct traditional, faith-based, and deeply spiritual religious wedding ceremonies tailored to your beliefs.",
          },
          {
            q: "What areas do you travel to for wedding officiating and mobile notary services?",
            a: "OGO Notary provides prompt mobile travel across the entirety of Brevard County and surrounding Space Coast regions, including Indialantic, Cocoa Beach, Melbourne, Palm Bay, Titusville, and Rockledge. We bring our professional services directly to your home, office, venue, beachside location, or care facility.",
          },
          {
            q: "Do you help with the name change paperwork after the wedding ceremony?",
            a: "Absolutely. This is the unique advantage of working with OGO Services. Beyond officiating your vows, we leverage our deep administrative and legal signing expertise to guide you through your post-ceremony transitions, including certified marriage certificate filings, legal name changes, Social Security records updates, and passport/identity modifications.",
          },
          {
            q: "How much does a wedding officiant cost in Brevard County, Florida?",
            a: "OGO Notary offers transparent flat-rate wedding officiating packages for Brevard County that vary by ceremony type — sign-and-go civil, elopement/micro-wedding, custom personalized ceremony, or vow renewal. Every package includes marriage license verification and execution. Travel to beachside venues along the Space Coast and after-hours ceremonies are quoted transparently up front. Contact Greg@ogonotary.com or call (855) 505-4722 for a written quote.",
          },
          {
            q: "Do you officiate beach weddings in Cocoa Beach and along the Brevard coast?",
            a: "Yes. Beach ceremonies along Cocoa Beach, Indialantic, Melbourne Beach, Satellite Beach, and the entire Space Coast are one of our most-requested wedding services. Dr. Byers officiates intimate sunrise ceremonies, sunset elopements, and full traditional beach weddings — coordinating with your photographer, venue, and any beach-permit requirements so you can focus on the moment.",
          },
        ],
      },
      {
        subtitle: "Adoption, Family Milestones & Life Documents",
        items: [
          {
            q: "Can you notarize adoption paperwork and family court documents?",
            a: "Yes. OGO Notary notarizes adoption consent forms, home-study affidavits, guardianship petitions, parental consent letters, and other family-court supporting documents in coordination with your family-law attorney or adoption agency. Domestic adoptions and international adoption paperwork requiring Apostille are both routinely handled — end to end.",
          },
          {
            q: "Can you notarize documents for a name change after marriage, divorce, or court order?",
            a: "Yes. OGO Notary regularly notarizes Petitions for Name Change, court-order acknowledgments, and the downstream identity documents that follow — Social Security records updates, passport application affidavits, and DMV name-change forms. For marriages, the notarized marriage certificate itself acts as your primary supporting evidence and requires no additional court order.",
          },
          {
            q: "Do you notarize documents for new parents — birth records, custody, or paternity?",
            a: "Yes. OGO Notary notarizes voluntary paternity acknowledgments, custody-related affidavits, birth-record affidavits, and related family documents. For international adoptions or U.S. birth records that need to be presented overseas, we can also add the Apostille from the Florida Secretary of State to complete the packet.",
          },
        ],
      },
    ],
  },
  {
    pillar: "Pillar 5 · Elder Care & Senior Specialist Services",
    intro:
      "Combining Senior Living Transition & Patient Advocacy — a compassionate, disciplined, and dignified pillar built for our senior community and their families. Certified Senior Advisor (CSA) Candidate and End-of-Life (EoL) Doula Candidate credentials guide every visit.",
    sections: [
      {
        subtitle: "Bedside, Hospice & Assisted-Living Notarization",
        items: [
          {
            q: "Can you notarize documents for a family member in a nursing home or hospice?",
            a: "Yes. OGO Notary provides bedside mobile notary service throughout Brevard County for family members in hospitals, hospices, rehab centers, and assisted-living or memory-care communities. We coordinate with facility staff, verify the signer's identity, and confirm the signer is signing willingly and with apparent awareness as required by Florida law. Same-day and after-hours appointments are available for urgent situations.",
          },
          {
            q: "What if my parent is very ill or has limited mobility — can they still sign?",
            a: "In most cases, yes. Florida law requires that the signer personally appears before the notary, is aware of what they are signing, and signs willingly. A signer with limited mobility can sign propped up in a hospital bed, seated in a wheelchair, or with a signature guide. If the signer is physically unable to sign, Florida allows a signature by mark or by directed proxy under specific conditions — OGO Notary can walk you through the requirements before the visit.",
          },
          {
            q: "What happens if the signer has dementia or Alzheimer's — can they still be notarized?",
            a: "It depends on the signer's cognitive state at the moment of signing. Florida law requires the notary to confirm the signer understands the document and is signing willingly. If the signer has a lucid interval and can demonstrate awareness of the document and their action, the notarization can proceed. If the signer cannot demonstrate awareness, the notarization must be declined regardless of family circumstances. OGO Notary approaches every elder-care visit with patience, unhurried care, and a CSA-Candidate-informed lens.",
          },
        ],
      },
      {
        subtitle: "POAs, Healthcare Surrogates & End-of-Life Planning",
        items: [
          {
            q: "Can OGO Notary notarize a Power of Attorney or Healthcare Surrogate?",
            a: "Yes. OGO Notary regularly notarizes durable powers of attorney, springing powers of attorney, healthcare surrogate designations, HIPAA authorizations, and living wills — either in-person at your home, office, hospital, or senior-care facility, or remotely by RON for eligible documents. Two witnesses are required for healthcare surrogates and can be arranged in advance.",
          },
          {
            q: "What documents should every senior have notarized before a medical event?",
            a: "The core elder-care documents most families notarize together are: Durable Power of Attorney (financial/legal), Healthcare Surrogate Designation (medical decisions), Living Will / Advance Directive (end-of-life preferences), HIPAA Authorization (medical record access), and — where appropriate — a self-proving affidavit on the will and beneficiary designations on retirement accounts. OGO Notary handles all five at a single coordinated appointment.",
          },
          {
            q: "Can you notarize guardianship or conservatorship documents?",
            a: "Yes. OGO Notary notarizes guardianship petitions, consents, and supporting affidavits filed in Florida Probate Court — coordinated closely with your elder-law attorney. Emergency (temporary) guardianship documents can typically be handled on a same-day basis when the medical situation requires immediate court filing.",
          },
          {
            q: "What is an End-of-Life (EoL) Doula, and how does that credential help?",
            a: "An End-of-Life Doula is a non-medical companion trained to guide individuals and their families through the emotional, practical, and administrative dimensions of the end-of-life journey. Dr. Byers's EoL Doula Candidate training brings unhurried presence, active listening, and a working knowledge of hospice workflows to every elder-care visit — so families feel supported, not rushed, when the notarization is only part of a much larger moment.",
          },
        ],
      },
    ],
  },
];

// Non-pillar operational FAQ — booking mechanics + hours. Retained as its own
// footer bucket so visitors can find scheduling info without scrolling the
// entire pillar tree.
const OPERATIONS_SECTION = {
  title: "Pricing, Booking & Hours",
  items: [
    {
      q: "How do I book an appointment with OGO Notary?",
      a: "Book an appointment with OGO Notary three ways: fill out the Contact form at OGONotary.com/contact, email Greg@ogonotary.com directly, or call (855) 505-4722. Please include the document type, your Brevard County location (or RON preference), and your preferred date/time. Most inquiries receive a same-business-day reply.",
    },
    {
      q: "What are your business hours and do you take weekend appointments?",
      a: "OGO Notary operates by appointment seven days a week, including evenings and weekends. Mobile appointments are typically scheduled between 8 AM and 9 PM local Florida time. Same-day, after-hours, and holiday appointments are accepted subject to availability with a transparent rush or convenience fee disclosed up front.",
    },
    {
      q: "How much does OGO Notary charge, and are your fees Florida-compliant?",
      a: "OGO Notary charges the Florida statutory maximum per notarized signature (currently $10 per signature) plus a transparent travel, convenience, and — where applicable — signing-package or Apostille handling fee. All statutory notarial fees comply with Florida law. Every quote is delivered in writing before the appointment. See OGONotary.com/pricing or email Greg@ogonotary.com for a personalized quote.",
    },
  ],
};

// TOC anchor slugs — keep in sync with the id attribute added to each pillar
// block below. These drive the sticky jump-nav at the top of the FAQ page.
const TOC_ITEMS = [
  { id: "pillar-1", label: "P1 · Mobile & RON" },
  { id: "pillar-2", label: "P2 · Real Estate & Estate Planning" },
  { id: "pillar-3", label: "P3 · Gov & Apostille" },
  { id: "pillar-4", label: "P4 · New Beginnings" },
  { id: "pillar-5", label: "P5 · Elder Care" },
  { id: "operations", label: "Pricing · Booking · Hours" },
];

// Flatten every Q&A across the 5 Pillars + operations bucket into a single
// FAQPage JSON-LD block so Google, Bing, ChatGPT, Perplexity, and other AI /
// search platforms can lift these answers as featured-snippet / AI-overview
// rich results.
const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    ...PILLARS.flatMap((p) =>
      p.sections.flatMap((sec) =>
        sec.items.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        }))
      )
    ),
    ...OPERATIONS_SECTION.items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  ],
};

export default function FAQPage() {
  return (
    <>
      {/* SEO: FAQPage structured data for rich-result eligibility */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      <PageHero
        eyebrow="Frequently Asked Questions"
        title="Mobile Notary, RON, Apostille & More — Brevard County FAQ."
        lead="Real, plain-English answers grouped by the 5 Pillars of OGO Excellence. If you can't find your answer below, email Greg@ogonotary.com or call (855) 505-4722 — most inquiries get a same-business-day reply."
        backdrop={IMG.notaryStamp}
        backdropOpacity={0.5}
        bright
      />

      {/* Table of Contents — sticky jump-nav on desktop, wrappable pill row on
          mobile. Anchor-scrolls to each pillar block below. */}
      <div
        data-testid="faq-toc"
        className="sticky top-0 z-30 bg-[#142339]/95 backdrop-blur border-b border-white/10"
      >
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold pr-2">
              Jump to ·
            </span>
            {TOC_ITEMS.map((t) => (
              <a
                key={t.id}
                href={`#${t.id}`}
                data-testid={`faq-toc-${t.id}`}
                className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-white/15 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-medium text-slate-300 hover:text-[#D4AF37] transition-colors"
              >
                {t.label}
              </a>
            ))}
          </div>
        </nav>
      </div>

      <section className="relative overflow-hidden py-24 sm:py-32">
        <FadedBackdrop src={IMG.signingTable} opacity={0.18} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 space-y-24">
          {PILLARS.map((p, pIdx) => (
            <PillarBlock key={pIdx} pillar={p} pIdx={pIdx} />
          ))}

          {/* Operations / booking mechanics — non-pillar footer bucket */}
          <OperationsBlock section={OPERATIONS_SECTION} pIdx={PILLARS.length} />

          <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="eyebrow mb-2">Still have a question?</div>
              <p className="font-serif-display text-2xl text-white max-w-xl">
                Send us a quick message — most inquiries get a same-business-day
                reply.
              </p>
            </div>
            <GoldButton as={Link} to="/contact" data-testid="faq-contact-btn">
              Contact Us <ArrowUpRight className="w-4 h-4" />
            </GoldButton>
          </div>
        </div>
      </section>
    </>
  );
}

function PillarBlock({ pillar, pIdx }) {
  return (
    <div
      id={`pillar-${pIdx + 1}`}
      data-testid={`faq-pillar-${pIdx}`}
      className="scroll-mt-32"
    >
      <SectionEyebrow className="mb-3">
        {String(pIdx + 1).padStart(2, "0")}
      </SectionEyebrow>
      <h2 className="font-serif-display text-3xl sm:text-4xl text-white mb-4 tracking-tight">
        {pillar.pillar}
      </h2>
      {pillar.intro && (
        <p className="text-slate-400 leading-relaxed max-w-3xl mb-10 text-base">
          {pillar.intro}
        </p>
      )}

      <div className="space-y-10">
        {pillar.sections.map((sec, sIdx) => (
          <div key={sIdx} data-testid={`faq-pillar-${pIdx}-sub-${sIdx}`}>
            <h3 className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-semibold mb-4 border-l-2 border-[#D4AF37] pl-3">
              {sec.subtitle}
            </h3>
            <Accordion
              type="single"
              collapsible
              className="border border-white/10 divide-y divide-white/10"
            >
              {sec.items.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`p${pIdx}-s${sIdx}-${i}`}
                  className="border-0 px-6"
                >
                  <AccordionTrigger
                    data-testid={`faq-q-${pIdx}-${sIdx}-${i}`}
                    className="text-left font-serif-display text-lg sm:text-xl text-white hover:text-[#D4AF37] hover:no-underline py-5"
                  >
                    <span className="block">{item.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 text-base leading-relaxed pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
}

function OperationsBlock({ section, pIdx }) {
  return (
    <div
      id="operations"
      data-testid="faq-operations"
      className="scroll-mt-32"
    >
      <SectionEyebrow className="mb-3">
        {String(pIdx + 1).padStart(2, "0")}
      </SectionEyebrow>
      <h2 className="font-serif-display text-3xl sm:text-4xl text-white mb-8 tracking-tight">
        {section.title}
      </h2>
      <Accordion
        type="single"
        collapsible
        className="border border-white/10 divide-y divide-white/10"
      >
        {section.items.map((item, i) => (
          <AccordionItem key={i} value={`ops-${i}`} className="border-0 px-6">
            <AccordionTrigger
              data-testid={`faq-ops-q-${i}`}
              className="text-left font-serif-display text-lg sm:text-xl text-white hover:text-[#D4AF37] hover:no-underline py-5"
            >
              <span className="block">{item.q}</span>
            </AccordionTrigger>
            <AccordionContent className="text-slate-300 text-base leading-relaxed pb-5">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
